// utils/excelParser.js
import { showError, showSuccess } from "../components/AppToast";
import * as XLSX from "xlsx";

const SAMPLE_ROWS = 100;
const MAX_ENUM_VALUES = 8;
const MAX_ENUM_RATIO = 0.3;
const MIN_ENUM_ROWS = 5;
const MAX_OPTION_LENGTH = 40;
const MAX_CELL_LENGTH = 250;
const MAX_HEADER_LENGTH = 100;


// ── Header-name patterns ──────────────────────────────────────────────────────
const HEADER_PATTERNS = {
  name: /\b(name|student_name|fathers_name|father_name|mothers_name|mother_name|guardian|candidate|admission_number|admission)\b/i,
  email: /\b(email|e_?mail|mail)\b/i,
  phone: /\b(phone|mobile|cell|contact|tel|ph_?no|phno|mobile_number)\b/i,
  date: /\b(date|dob|birth|created|updated|joined|expiry|deadline|dt)\b/i,
  amount:
    /\b(amount|fee|fees|tution|tuition|charges|payment|price|salary|total|sum|rate|cost|fine|penalty|tax|dues|deposit|stipend|remuneration|payable|receivable|revenue|income|expenditure|budget|balance|outstanding)\b/i,
  number:
    /\b(no|num|number|id|roll|rank|score|marks|grade|age|qty|quantity|count|percent|cgpa|gpa|code|pin|admission)\b/i,
  checkbox: /\b(active|enabled|verified|confirmed|agree|consent|flag)\b/i,
  address: /\b(address|addr|street|road|lane|avenue|ave|boulevard|blvd|locality|area|district|city|town|village|state|province|region|country|zipcode|zip|postal\s?code|postcode|pincode|pin\s?code|location|residence|residential|mailing|billing|shipping)\b/i,
  textarea:
    /\b(description|desc|remarks|notes?|comment|address|feedback|bio|summary|detail|message|reason|purpose|qualification|experience)\b/i,
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^[+\-()\s\d]{7,15}$/;
const DATE_RE =
  /^\d{1,4}[-/]\d{1,2}[-/]\d{1,4}$|^\d{1,2}[-/]\w{3}[-/]\d{2,4}$/i;
const NUMBER_RE = /^-?\d+(\.\d+)?$/;
const BOOL_VALUES = new Set([
  "yes",
  "no",
  "true",
  "false",
  "1",
  "0",
  "y",
  "n",
  "active",
  "inactive",
]);

export function normalizeHeader(str) {
  return str
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[''`]/g, "")
    .replace(/[^a-z0-9\s_]/g, " ")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

export function humanizeHeader(str) {
  return str
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/[^a-zA-Z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(
      /\w\S*/g,
      (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
    );
}

// ─────────────────────────────────────────────────────────────────────────────

function dedupe(arr) {
  return [...new Set(arr.map((v) => String(v).trim()).filter(Boolean))];
}

function matchRatio(samples, testFn) {
  if (!samples.length) return 0;
  return (
    samples.filter((v) => testFn(String(v).trim())).length / samples.length
  );
}

function detectType(normalizedId, samples) {
  const h = normalizedId.toLowerCase();

  if (HEADER_PATTERNS.name.test(h)) return { type: "text" };
  if (HEADER_PATTERNS.email.test(h)) return { type: "email" };
  if (HEADER_PATTERNS.phone.test(h)) return { type: "phone" };
  if (HEADER_PATTERNS.date.test(h)) return { type: "date" };
  if (HEADER_PATTERNS.number.test(h)) return { type: "address" };
  if (HEADER_PATTERNS.textarea.test(h)) return { type: "textarea" };
  if (HEADER_PATTERNS.checkbox.test(h)) return { type: "checkbox" };
  if (HEADER_PATTERNS.amount.test(h)) return { type: "amount" };
  if (HEADER_PATTERNS.number.test(h)) return { type: "number" };

  const strSamples = samples
    .map((v) => (v == null ? "" : String(v).trim()))
    .filter(Boolean);

  if (!strSamples.length) return { type: "text" };

  if (matchRatio(strSamples, (v) => EMAIL_RE.test(v)) >= 0.7)
    return { type: "email" };
  if (matchRatio(strSamples, (v) => PHONE_RE.test(v) && /\d{7}/.test(v)) >= 0.7)
    return { type: "phone" };
  if (matchRatio(strSamples, (v) => DATE_RE.test(v)) >= 0.7)
    return { type: "date" };
  if (matchRatio(strSamples, (v) => NUMBER_RE.test(v)) >= 0.8)
    return { type: "number" };
  if (matchRatio(strSamples, (v) => BOOL_VALUES.has(v.toLowerCase())) >= 0.7)
    return { type: "checkbox" };

  const avgLen =
    strSamples.reduce((s, v) => s + v.length, 0) / strSamples.length;
  if (avgLen > 80) return { type: "textarea" };

  const unique = dedupe(strSamples);
  const allShort = unique.every((v) => v.length <= MAX_OPTION_LENGTH);
  const ratio = unique.length / strSamples.length;

  if (
    strSamples.length >= MIN_ENUM_ROWS &&
    unique.length >= 2 &&
    unique.length <= MAX_ENUM_VALUES &&
    ratio <= MAX_ENUM_RATIO &&
    allShort
  ) {
    return { type: unique.length <= 4 ? "radio" : "select", options: unique };
  }

  return { type: "text" };
}

// ── Single-sheet guard ────────────────────────────────────────────────────────
function getOnlySheet(workbook) {
  if (workbook.SheetNames.length > 1) {
    const names = workbook.SheetNames.map((n) => `"${n}"`).join(", ");
    throw new Error(
      `This file contains ${workbook.SheetNames.length} sheets (${names}). ` +
        `Please upload a file with a single sheet only.`,
    );
  }
  return workbook.Sheets[workbook.SheetNames[0]];
}

// ── Guard: reject overly long headers or cell values ──────────────────────────

function validateLengths(rawRows) {
  const headers = rawRows[0] || [];
  for (const h of headers) {
    const len = h != null ? String(h).trim().length : 0;
    if (len > MAX_HEADER_LENGTH) {
      throw new Error("Column headers cannot exceed 100 characters.");
    }
  }

  for (let r = 1; r < rawRows.length; r++) {
    const row = rawRows[r];
    for (let c = 0; c < row.length; c++) {
      const val = row[c];
      const len = val != null ? String(val).trim().length : 0;

      if (len > MAX_CELL_LENGTH) {
        throw new Error(
          `Row ${r + 1}, Column ${c + 1} has a value longer than 250 characters.`,
        );
      }
    }
  }
}


// ─────────────────────────────────────────────────────────────────────────────
// Main export
// ─────────────────────────────────────────────────────────────────────────────
export async function parseExcelToFields(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, {
          type: "array",
          cellDates: true,
          raw: false,
        });

        let sheet;
        try {
          sheet = getOnlySheet(workbook);
        } catch (err) {
          return reject(err);
        }

        const rawRows = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: "",
          blankrows: false,
        });

        if (!rawRows.length)
          return reject(new Error("The file appears to be empty."));

        // Length guard
        try {
          validateLengths(rawRows);
        } catch (err) {
          return reject(err);
        }

        const rawHeaders = (rawRows[0] || [])
          .map((h) => normalizeHeader(h))
          .filter(Boolean);

                if (rawHeaders.length > 50) {
          return reject(new Error("Only 50 columns can be uploaded"));
        }

        // ── Guard: reject duplicate headers ───────────────────────────────
        const seenHeaders = new Set();
        for (const h of rawHeaders) {
          if (!h) continue;
          const normalized = normalizeHeader(h);
          if (seenHeaders.has(normalized)) {
            return reject(new Error(`Duplicate columns are not allowed`));
          }
          seenHeaders.add(normalized);
        }

        const validCols = rawHeaders
          .map((raw, colIdx) => ({
            raw,
            normalized: normalizeHeader(raw),
            colIdx
          }))
          .filter(({ raw }) => raw.length > 0);

        if (!validCols.length)
          return reject(new Error("No column headers found in row 1."));

        const usedIds = new Set();
        function uniqueId(base) {
          if (!usedIds.has(base)) {
            usedIds.add(base);
            return base;
          }
          let n = 2;
          while (usedIds.has(`${base}_${n}`)) n++;
          const uid = `${base}_${n}`;
          usedIds.add(uid);
          return uid;
        }

        const dataRows = rawRows
          .slice(1)
          .filter((r) => r.some((c) => c !== "" && c != null))
          .slice(0, SAMPLE_ROWS);

        const fields = validCols.map(({ raw, colIdx }) => {
          const normalizedId = uniqueId(
            normalizeHeader(raw) || `field_${colIdx}`,
          );
          const humanLabel = humanizeHeader(raw);

          const samples = dataRows
            .map((row) => row[colIdx])
            .filter((v) => v != null && v !== "");

          const { type, options } = detectType(normalizedId, samples);

          return {
            id: normalizedId,
            label: humanLabel,
            rawHeader: raw,
            type,
            options: options || [],
            required: false,
            mandatory: false,
            fromExcel: true,
          };
        });

        const rawToId = {};
        fields.forEach((f) => {
          rawToId[f.rawHeader] = f.id;
        });

        const rawObjectRows = XLSX.utils.sheet_to_json(sheet, {
          defval: "",
          blankrows: false,
          raw: false,
        });

        const rows = rawObjectRows.map((rawRow) => {
          const out = {};
          for (const [key, val] of Object.entries(rawRow)) {
            const trimmed = key.trim();
            out[rawToId[trimmed] || normalizeHeader(trimmed)] = val;
          }
          return out;
        });

        resolve({ fields, rows });
      } catch (err) {
        reject(new Error("Failed to parse file: " + err.message));
      }
    };

    reader.onerror = () => reject(new Error("Failed to read file."));
    reader.readAsArrayBuffer(file);
  });
}
